import { da, de, enGB, enUS, es, fr, nb, sv, type Locale } from 'date-fns/locale'

/**
 * The locale every number and date in the app is formatted in — the one picked
 * on the user's preferences page, not the browser's.
 *
 * It lives in a module variable rather than a context because most formatting
 * happens in plain functions outside the React tree (table cell renderers,
 * chart tick formatters, service helpers). `UserProvider` keeps it in step with
 * the signed-in user, and since it sets it while rendering above everything
 * else, a change re-renders the tree that reads it.
 */

const STORAGE_KEY = 'adnuntius.locale'
const FALLBACK = 'en-GB'

/** The API spells locales `en_GB`; `Intl` wants `en-GB`. */
function toBcp47(locale: string | undefined | null): string | null {
  if (!locale) return null
  const tag = locale.replace(/_/g, '-')
  try {
    // Anything Intl cannot canonicalise is not worth passing on to it later.
    return Intl.getCanonicalLocales(tag)[0] ?? null
  } catch {
    return null
  }
}

// Remembered so the first paint — before the current user has loaded — already
// formats the way the last session did, rather than flipping once it arrives.
let activeLocale = toBcp47(localStorage.getItem(STORAGE_KEY)) ?? FALLBACK

/** The locale to format in. Always a tag `Intl` accepts. */
export function getLocale(): string {
  return activeLocale
}

/** Points every formatter at the user's locale. Unknown tags are ignored. */
export function setLocale(locale: string | undefined | null): void {
  const next = toBcp47(locale)
  if (!next || next === activeLocale) return
  activeLocale = next
  localStorage.setItem(STORAGE_KEY, next)
}

/** The date-fns locales the preferences page offers, by language tag. */
const DATE_FNS_LOCALES: Record<string, Locale> = {
  'en-GB': enGB,
  'en-US': enUS,
  'nb-NO': nb,
  'sv-SE': sv,
  'da-DK': da,
  'de-DE': de,
  'fr-FR': fr,
  'es-ES': es,
}

/**
 * The active locale as date-fns spells it, for the calendar — which needs month
 * names and, more importantly, the day the week starts on. Falls back to the
 * bare language, then to British English.
 */
export function getDateFnsLocale(): Locale {
  const tag = getLocale()
  return DATE_FNS_LOCALES[tag] ?? DATE_FNS_LOCALES[tag.split('-')[0]] ?? enGB
}
